// ABOUTME: Cell renderer for ShDataGrid3 — calls column.renderCell if provided, otherwise renders the raw value.
// ABOUTME: Applies position: sticky for pinned columns; shadow overlays are handled by ShGridPinnedEdge, not here.

import { memo } from "react";

/**
 * ShGridCell
 *
 * Props:
 *   column        — the resolved code column definition ({ field, headerName, renderCell, width, flex, cellClassName, ... })
 *   row           — the full row object (including `id` and any fields)
 *   rowIndex      — current visible row index (0-based within the rendered window)
 *   pinnedOffset  — { offsetPx, side } or null; when present, the cell sticks to that edge
 *   className     — extra class names to apply (e.g. "sh-grid-cell--last-left-pinned")
 *
 * Performance note: this component is rendered once per visible cell (hundreds at a time
 * in a typical grid). It deliberately avoids MUI sx prop / styled components and uses
 * plain inline style objects. All visual styling (padding, border, hover, fonts) is owned
 * by the parent ShDataGrid3's single sx on the root element, using descendant selectors.
 */
const ShGridCell = memo(function ShGridCell({ column, row, rowIndex, pinnedOffset, className }) {
    // MUI-shaped params so existing column.renderCell functions keep working unchanged.
    // The ShDataGrid2 contract passes `{ id, row, value, field, colDef }` and grids rely on `params.row`.
    const value = column?.valueGetter
        ? column.valueGetter({ row, field: column.field })
        : row?.[column?.field];

    const params = {
        id: row?.id,
        row,
        value,
        field: column?.field,
        colDef: column,
        rowIndex,
    };

    const content = typeof column?.renderCell === "function"
        ? column.renderCell(params)
        : (value ?? null);

    const style = pinnedOffset
        ? {
              position: "sticky",
              [pinnedOffset.side]: `${pinnedOffset.offsetPx}px`,
              zIndex: 2,
          }
        : undefined;

    const classes = [
        "sh-grid-cell",
        pinnedOffset?.side === "left" && "sh-grid-cell--pinned-left",
        pinnedOffset?.side === "right" && "sh-grid-cell--pinned-right",
        pinnedOffset?.isSeam && pinnedOffset?.side === "left" && "sh-grid-cell--seam-left",
        pinnedOffset?.isSeam && pinnedOffset?.side === "right" && "sh-grid-cell--seam-right",
        column?.cellClassName,
        className,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className={classes} style={style} data-col={column?.field} data-row={rowIndex}>
            {content}
        </div>
    );
});

export default ShGridCell;
